import AdminLayout from '@/layouts/AdminLayout'
import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import Pagination from '@/components/common/Pagination'
import { clientService } from '@services/api'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }

const getPhotoUrl = (photo) => {
  if (!photo) return null
  if (photo.startsWith('http')) return photo
  return mediaUrl(photo)
}

function AdminClientsPage() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [countryFilter, setCountryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date_desc')
  const [filterByPublications, setFilterByPublications] = useState('all')
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [editClient, setEditClient] = useState({
    client_id: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    codeNumeroPays: '+225'
  })
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    codeNumeroPays: '+225',
    mot_de_passe: ''
  })
  
  const [selectedClients, setSelectedClients] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  
  // États de pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(6)

  // Fonctions utilitaires pour les pays
  const getCountryFlag = (phoneCode) => {
    const flags = {
      '+225': 'CI',
      '+226': 'BF',
      '+227': 'NE',
      '+228': 'TG',
      '+229': 'BJ',
      '+221': 'SN',
      '+223': 'ML',
      '+224': 'GN',
      '+237': 'CM',
      '+242': 'CG',
      '+243': 'CD',
      '+33': 'FR'
    }
    return flags[phoneCode] || 'XX'
  }
  
  const getFlagEmoji = (countryCode) => {
    // Convertir le code pays en émoji drapeau de manière compatible
    if (countryCode === 'XX') return '🌍'
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt())
    return String.fromCodePoint(...codePoints)
  }

  const getCountryName = (phoneCode) => {
    const countries = {
      '+225': 'Côte d\'Ivoire',
      '+226': 'Burkina Faso',
      '+227': 'Niger',
      '+228': 'Togo',
      '+229': 'Bénin',
      '+221': 'Sénégal',
      '+223': 'Mali',
      '+224': 'Guinée',
      '+237': 'Cameroun',
      '+242': 'Congo',
      '+243': 'RD Congo',
      '+33': 'France'
    }
    return countries[phoneCode] || 'Inconnu'
  }

  useEffect(() => {
    loadClients()

    // Écouter l'événement d'ouverture du modal depuis le layout
    const handleOpenModal = () => setShowAddClientModal(true)
    window.addEventListener('openAddClientModal', handleOpenModal)

    return () => {
      window.removeEventListener('openAddClientModal', handleOpenModal)
    }
  }, [])

  const loadClients = async () => {
    try {
      const result = await clientService.getClients()
      if (result?.success !== false) {
        setClients(result.data || result.clients || [])
      }
      setLoading(false)
    } catch (error) {
      console.error('Erreur:', error)
      setLoading(false)
    }
  }

  const handleViewClient = (client) => {
    setSelectedClient(client)
    setShowViewModal(true)
  }

  const handleEditClient = (client) => {
    setEditClient({
      client_id: client.client_id,
      nom: client.nom || '',
      prenom: client.prenom || '',
      email: client.email || '',
      telephone: client.telephone || client.numero || '',
      codeNumeroPays: client.codeNumeroPays || '+225'
    })
    setShowEditModal(true)
  }

  const handleUpdateClient = async () => {
    // Validation
    if (!editClient.nom || !editClient.prenom || !editClient.email) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(editClient.email)) {
      toast.error('Veuillez entrer un email valide')
      return
    }

    try {
      const result = await clientService.updateClient(editClient.client_id, {
        nom: editClient.nom,
        prenom: editClient.prenom,
        email: editClient.email,
        numero: editClient.telephone,
        codeNumeroPays: editClient.codeNumeroPays
      })

      if (result?.success !== false) {
        toast.success('Client modifié avec succès')
        setShowEditModal(false)
        setEditClient({
          client_id: '',
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          codeNumeroPays: '+225'
        })
        loadClients()
      } else {
        toast.error(result.message || 'Erreur lors de la modification du client')
      }
    } catch (error) {
      console.error('Error updating client:', error)
      const errorMessage = error.response?.data?.message || 'Erreur lors de la modification du client'
      toast.error(errorMessage)
    }
  }

  const handleArchiveClient = async (clientId, isArchived) => {
    try {
      await clientService.archiveClient(clientId)
      toast.success('Opération d\'archivage réussie')
      loadClients()
      setShowArchiveModal(false)
      setSelectedClient(null)
    } catch (error) {
      console.error('Error archiving client:', error)
      toast.error(`Erreur lors de l'archivage du client`)
    }
  }

  const handleDeleteClient = async (clientId) => {
    try {
      await clientService.deleteClient(clientId)
      toast.success('Client supprimé')
      loadClients()
      setShowDeleteModal(false)
      setSelectedClient(null)
    } catch (error) {
      console.error('Error deleting client:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression du client')
    }
  }

  const handleAddClient = async () => {
    // Validation
    if (!newClient.nom || !newClient.prenom || !newClient.email || !newClient.telephone || !newClient.mot_de_passe) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newClient.email)) {
      toast.error('Veuillez entrer un email valide')
      return
    }

    // Validation mot de passe (minimum 8 caractères comme register.php)
    if (newClient.mot_de_passe.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères')
      return
    }

    try {
      await clientService.createClient({
        nom: newClient.nom,
        prenom: newClient.prenom,
        email: newClient.email,
        numero: newClient.telephone,
        codeNumeroPays: newClient.codeNumeroPays,
        password: newClient.mot_de_passe
      })
      toast.success('Client créé avec succès')
      setShowAddClientModal(false)
      setNewClient({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        codeNumeroPays: '+225',
        mot_de_passe: ''
      })
      loadClients()
    } catch (error) {
      console.error('Error adding client:', error)
      const errorMessage = error.response?.data?.message || 'Erreur lors de la création du client'
      toast.error(errorMessage)
    }
  }

  const filteredClients = clients.filter(client => {
    // Afficher les clients : avec rôle "Client" OU sans rôle du tout
    const userRoles = client.roles ? client.roles.toLowerCase() : ''
    const isClient = userRoles.includes('client')
    const hasNoRole = !client.roles || client.roles.trim() === ''
    const isStaffOrAdmin = userRoles.includes('super admin') || 
                           userRoles.includes('admin') || 
                           userRoles.includes('superviseur') ||
                           userRoles.includes('animateur') ||
                           userRoles.includes('technicien')
    
    // Afficher ceux avec rôle Client OU sans rôle (mais pas les staff/admins)
    if (isStaffOrAdmin) return false
    if (!isClient && !hasNoRole) return false
    
    // Filtre par statut (actif/archivé/tous)
    if (statusFilter === 'active' && client.archived) return false
    if (statusFilter === 'archived' && !client.archived) return false
    
    // Filtre par pays
    const clientCountry = client.codeNumeroPays || '+225'
    if (countryFilter !== 'all' && clientCountry !== countryFilter) return false
    
    // Filtre par publications
    const postCount = client.posts_count || 0
    if (filterByPublications === 'with_posts' && postCount === 0) return false
    if (filterByPublications === 'without_posts' && postCount > 0) return false
    if (filterByPublications === 'more_than_5' && postCount <= 5) return false
    if (filterByPublications === 'more_than_10' && postCount <= 10) return false
    
    // Appliquer le filtre de recherche
    return client.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           client.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  })
  
  // Appliquer le tri
  const sortedClients = [...filteredClients].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.date_inscription || 0) - new Date(a.date_inscription || 0)
      case 'date_asc':
        return new Date(a.date_inscription || 0) - new Date(b.date_inscription || 0)
      case 'name_asc':
        return (a.nom || '').localeCompare(b.nom || '')
      case 'name_desc':
        return (b.nom || '').localeCompare(a.nom || '')
      case 'posts_desc':
        return (b.posts_count || 0) - (a.posts_count || 0)
      case 'posts_asc':
        return (a.posts_count || 0) - (b.posts_count || 0)
      default:
        return 0
    }
  })
  
  // Calculer les éléments paginés
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedClients = sortedClients.slice(startIndex, endIndex)
  
  // Réinitialiser à la page 1 lors d'un changement de filtre
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, countryFilter, sortBy, filterByPublications])
  
  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Gestion des Clients</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Gérer les clients de la plateforme
              </p>
            </div>
            <button 
              onClick={() => setShowAddClientModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Nouveau client
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Tous les clients */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total Clients</p>
                <p className="text-2xl font-bold text-gray-800">{filteredClients.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Clients actifs */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Clients Actifs</p>
                <p className="text-2xl font-bold text-green-600">
                  {filteredClients.filter(c => !c.archived).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Clients archivés */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Clients Archivés</p>
                <p className="text-2xl font-bold text-orange-600">
                  {filteredClients.filter(c => c.archived).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="relative md:col-span-2 lg:col-span-1">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all"
              />
            </div>

            {/* Filtre par statut */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all bg-white"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">✓ Actifs</option>
              <option value="archived">📦 Archivés</option>
            </select>

            {/* Filtre par pays */}
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all bg-white"
            >
              <option value="all">Tous les pays</option>
              <option value="+225">{getFlagEmoji('CI')} Côte d'Ivoire</option>
              <option value="+226">{getFlagEmoji('BF')} Burkina Faso</option>
              <option value="+227">{getFlagEmoji('NE')} Niger</option>
              <option value="+228">{getFlagEmoji('TG')} Togo</option>
              <option value="+229">{getFlagEmoji('BJ')} Bénin</option>
              <option value="+221">{getFlagEmoji('SN')} Sénégal</option>
              <option value="+223">{getFlagEmoji('ML')} Mali</option>
              <option value="+224">{getFlagEmoji('GN')} Guinée</option>
              <option value="+237">{getFlagEmoji('CM')} Cameroun</option>
              <option value="+242">{getFlagEmoji('CG')} Congo</option>
              <option value="+243">{getFlagEmoji('CD')} RD Congo</option>
              <option value="+33">{getFlagEmoji('FR')} France</option>
            </select>

            {/* Tri */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all bg-white"
            >
              <option value="date_desc">📅 Plus récents</option>
              <option value="date_asc">📅 Plus anciens</option>
              <option value="name_asc">🔤 Nom A-Z</option>
              <option value="name_desc">🔤 Nom Z-A</option>
              <option value="posts_desc">📝 Plus de publications</option>
              <option value="posts_asc">📝 Moins de publications</option>
            </select>

            {/* Filtre par publications */}
            <select
              value={filterByPublications}
              onChange={(e) => setFilterByPublications(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all bg-white"
            >
              <option value="all">Toutes les publications</option>
              <option value="with_posts">Avec publications</option>
              <option value="without_posts">Sans publications</option>
              <option value="more_than_5">Plus de 5 publications</option>
              <option value="more_than_10">Plus de 10 publications</option>
            </select>
          </div>
        </div>

        {/* Table des clients */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Liste des clients</h2>
              <span className="text-sm text-gray-500">{sortedClients.length} client{sortedClients.length > 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        setSelectAll(e.target.checked)
                        if (e.target.checked) {
                          setSelectedClients(paginatedClients.map(c => c.client_id))
                        } else {
                          setSelectedClients([])
                        }
                      }}
                      className="w-4 h-4 text-[#001F3F] border-gray-300 rounded focus:ring-2 focus:ring-[#001F3F] cursor-pointer"
                    />
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">N°</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Nom</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Téléphone</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Pays</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Publications</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Date inscription</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-500">
                      Chargement...
                    </td>
                  </tr>
                ) : filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-500">
                      Aucun client trouvé
                    </td>
                  </tr>
                ) : (
                  paginatedClients.map((client, index) => {
                    const phoneCode = client.codeNumeroPays || '+225'
                    
                    return (
                    <tr key={client.client_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.client_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedClients([...selectedClients, client.client_id])
                            } else {
                              setSelectedClients(selectedClients.filter(id => id !== client.client_id))
                              setSelectAll(false)
                            }
                          }}
                          className="w-4 h-4 text-[#001F3F] border-gray-300 rounded focus:ring-2 focus:ring-[#001F3F] cursor-pointer"
                        />
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{String((currentPage - 1) * itemsPerPage + index + 1).padStart(5, '0')}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          {(() => {
                            const photoUrl = getPhotoUrl(client.photo_profil || client.photo)
                            const initials = `${client.prenom?.[0] || ''}${client.nom?.[0] || ''}`.toUpperCase()
                            return photoUrl ? (
                              <div className="relative w-10 h-10 flex-shrink-0">
                                <img
                                  src={photoUrl}
                                  alt={`${client.prenom} ${client.nom}`}
                                  className="w-10 h-10 rounded-full object-cover border-2 border-[#D4AF37]/40 shadow-sm absolute inset-0"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.nextElementSibling.style.display = 'flex'
                                  }}
                                />
                                <div
                                  className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-full items-center justify-center"
                                  style={{ display: 'none' }}
                                >
                                  <span className="text-white text-sm font-bold">{initials}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                <span className="text-white text-sm font-bold">{initials}</span>
                              </div>
                            )
                          })()}
                          <div>
                            <p className="font-medium">{client.prenom} {client.nom}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">{client.email}</td>
                      <td className="py-4 px-6 text-gray-600">{client.telephone || client.numero || 'N/A'}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{getFlagEmoji(getCountryFlag(phoneCode))}</span>
                          <span className="text-sm text-gray-700">{getCountryName(phoneCode)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {client.posts_count || 0}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-600 text-sm">
                        {client.date_inscription 
                          ? new Date(client.date_inscription).toLocaleDateString('fr-FR')
                          : client.created_at 
                            ? new Date(client.created_at).toLocaleDateString('fr-FR')
                            : 'N/A'
                        }
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          {/* Voir */}
                          <button 
                            onClick={() => handleViewClient(client)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group relative"
                            title="Voir le profil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>

                          {/* Modifier */}
                          <button
                            onClick={() => handleEditClient(client)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors group relative"
                            title="Modifier le profil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          {/* Archiver/Désarchiver */}
                          <button 
                            onClick={() => {
                              setSelectedClient(client)
                              setShowArchiveModal(true)
                            }}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors group relative"
                            title={client.archived ? "Désarchiver" : "Archiver"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>

                          {/* Supprimer */}
                          <button 
                            onClick={() => {
                              setSelectedClient(client)
                              setShowDeleteModal(true)
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors group relative"
                            title="Supprimer le client"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {filteredClients.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">👥</div>
                <p className="text-lg">Aucun client trouvé</p>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {filteredClients.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredClients.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Modal Ajouter Client */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Ajouter un nouveau client</h2>
              </div>
              <button
                onClick={() => {
                  setShowAddClientModal(false)
                  setNewClient({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    codeNumeroPays: '+225',
                    mot_de_passe: ''
                  })
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newClient.nom}
                    onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                    placeholder="Entrez le nom"
                  />
                </div>

                {/* Prénom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newClient.prenom}
                    onChange={(e) => setNewClient({...newClient, prenom: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                    placeholder="Entrez le prénom"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                    placeholder="exemple@email.com"
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={newClient.codeNumeroPays || '+225'}
                      onChange={(e) => setNewClient({...newClient, codeNumeroPays: e.target.value})}
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-gray-50 font-medium"
                    >
                      <option value="+225">🇨🇮 +225</option>
                      <option value="+226">🇧🇫 +226</option>
                      <option value="+227">🇳🇪 +227</option>
                      <option value="+228">🇹🇬 +228</option>
                      <option value="+229">🇧🇯 +229</option>
                      <option value="+221">🇸🇳 +221</option>
                      <option value="+223">🇲🇱 +223</option>
                      <option value="+224">🇬🇳 +224</option>
                      <option value="+237">🇨🇲 +237</option>
                      <option value="+242">🇨🇬 +242</option>
                      <option value="+243">🇨🇩 +243</option>
                      <option value="+33">🇫🇷 +33</option>
                    </select>
                    <input
                      type="tel"
                      value={newClient.telephone}
                      onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      placeholder="XX XX XX XX XX"
                      required
                    />
                  </div>
                </div>

                {/* Mot de passe */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mot de passe <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={newClient.mot_de_passe}
                    onChange={(e) => setNewClient({...newClient, mot_de_passe: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                    placeholder="Mot de passe sécurisé"
                    minLength="8"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères requis</p>
                </div>
              </div>

              {/* Informations sur les privilèges */}
              <div className="mt-6 space-y-3">
                {/* Période gratuite */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800">Période d'essai gratuite</p>
                    <p className="text-xs text-blue-600 mt-1">30 jours d'accès gratuit à toutes les fonctionnalités</p>
                  </div>
                </div>

                {/* Privilèges accordés */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">Privilèges identiques à l'inscription</p>
                    <ul className="text-xs text-green-600 mt-1 space-y-1">
                      <li>✓ Rôle "Client" assigné automatiquement</li>
                      <li>✓ Token API généré pour l'authentification</li>
                      <li>✓ Accès complet à la plateforme</li>
                      <li>✓ Peut publier et gérer ses annonces</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-4 p-6 rounded-2xl border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowAddClientModal(false)
                  setNewClient({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    codeNumeroPays: '+225',
                    mot_de_passe: ''
                  })
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleAddClient}
                className="px-6 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Créer le client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Client */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Modifier le client</h2>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditClient({
                    client_id: '',
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    codeNumeroPays: '+225'
                  })
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editClient.nom}
                    onChange={(e) => setEditClient({...editClient, nom: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="Entrez le nom"
                  />
                </div>

                {/* Prénom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editClient.prenom}
                    onChange={(e) => setEditClient({...editClient, prenom: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="Entrez le prénom"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={editClient.email}
                    onChange={(e) => setEditClient({...editClient, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="exemple@email.com"
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={editClient.codeNumeroPays || '+225'}
                      onChange={(e) => setEditClient({...editClient, codeNumeroPays: e.target.value})}
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50 font-medium"
                    >
                      <option value="+225">🇨🇮 +225</option>
                      <option value="+226">🇧🇫 +226</option>
                      <option value="+227">🇳🇪 +227</option>
                      <option value="+228">🇹🇬 +228</option>
                      <option value="+229">🇧🇯 +229</option>
                      <option value="+221">🇸🇳 +221</option>
                      <option value="+223">🇲🇱 +223</option>
                      <option value="+224">🇬🇳 +224</option>
                      <option value="+237">🇨🇲 +237</option>
                      <option value="+242">🇨🇬 +242</option>
                      <option value="+243">🇨🇩 +243</option>
                      <option value="+33">🇫🇷 +33</option>
                    </select>
                    <input
                      type="tel"
                      value={editClient.telephone}
                      onChange={(e) => setEditClient({...editClient, telephone: e.target.value})}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="XX XX XX XX XX"
                    />
                  </div>
                </div>
              </div>

              {/* Info modification */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Modification du profil</p>
                  <p className="text-xs text-blue-600 mt-1">Les modifications seront appliquées immédiatement au compte du client</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditClient({
                    client_id: '',
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    codeNumeroPays: '+225'
                  })
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateClient}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualisation */}
      {showViewModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8 mx-4 shadow-2xl max-h-[90vh] overflow-y-auto animate-scaleIn">
            <div className="flex items-start justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                Profil du client
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedClient(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Photo de profil */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {selectedClient.prenom?.[0]}{selectedClient.nom?.[0]}
                </div>
              </div>

              {/* Informations personnelles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Nom complet</p>
                  <p className="font-semibold text-gray-800">{selectedClient.prenom} {selectedClient.nom}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-semibold text-gray-800">{selectedClient.email}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                  <p className="font-semibold text-gray-800">{selectedClient.telephone || selectedClient.numero || 'N/A'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Date d'inscription</p>
                  <p className="font-semibold text-gray-800">
                    {selectedClient.date_inscription 
                      ? new Date(selectedClient.date_inscription).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : selectedClient.created_at 
                        ? new Date(selectedClient.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : 'N/A'
                    }
                  </p>
                </div>
              </div>

              {/* Statistiques */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {selectedClient.posts_count || 0}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Publications</p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {selectedClient.archived ? 'Archivé' : 'Actif'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Statut</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedClient(null)
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'archivage */}
      {showArchiveModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full my-8 mx-4 shadow-2xl animate-scaleIn">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {selectedClient.archived ? 'Désarchiver' : 'Archiver'} le client
              </h3>
              <p className="text-gray-600">
                {selectedClient.archived 
                  ? `Voulez-vous réactiver le compte de ${selectedClient.prenom} ${selectedClient.nom} ?`
                  : `Êtes-vous sûr de vouloir archiver le compte de ${selectedClient.prenom} ${selectedClient.nom} ?`
                }
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-orange-800">
                {selectedClient.archived 
                  ? '✓ Le client pourra à nouveau se connecter et publier.'
                  : '⚠️ Le client ne pourra plus se connecter ni publier de contenu.'
                }
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveModal(false)
                  setSelectedClient(null)
                }}
                className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={() => handleArchiveClient(selectedClient.client_id, selectedClient.archived)}
                className="px-8 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                {selectedClient.archived ? 'Désarchiver' : 'Archiver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full my-8 mx-4 shadow-2xl animate-shake">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Supprimer le client
              </h3>
              <p className="text-gray-600">
                Êtes-vous sûr de vouloir supprimer définitivement le compte de{' '}
                <span className="font-semibold">{selectedClient.prenom} {selectedClient.nom}</span> ?
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 font-medium mb-2">⚠️ Action irréversible</p>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Toutes les publications seront supprimées</li>
                <li>• Les données du client seront perdues</li>
                <li>• Cette action ne peut pas être annulée</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedClient(null)
                }}
                className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteClient(selectedClient.client_id)}
                className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminClientsPage
